import React from "react";
import { stateChangeTracker, upsertStateChange } from "./utils";
export function useStateEffect() {
    const state = arguments[0];
    const transitions = arguments[1];
    const deps = Array.isArray(arguments[2]) ? arguments[2] : [];
    return React.useEffect(() => {
        var _a, _b, _c;
        const stateChange = stateChangeTracker.get(state);
        const currentState = state;
        const prevState = stateChange === null || stateChange === void 0 ? void 0 : stateChange.prevState;
        const action = stateChange === null || stateChange === void 0 ? void 0 : stateChange.action;
        let cb;
        if (typeof transitions[currentState.state] === "function") {
            cb = transitions[currentState.state];
        }
        else if (action &&
            typeof ((_a = transitions[currentState.state]) === null || _a === void 0 ? void 0 : _a[action.type]) === "function") {
            cb = transitions[currentState.state][action.type];
        }
        else if (action &&
            prevState &&
            typeof ((_c = (_b = transitions[currentState.state]) === null || _b === void 0 ? void 0 : _b[action.type]) === null || _c === void 0 ? void 0 : _c[prevState.state]) === "function") {
            cb = transitions[currentState.state][action.type][prevState.state];
        }
        if (cb) {
            const disposer = cb(currentState, action, prevState);
            return () => {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                stateChangeTracker.delete(state);
            };
        }
        return () => {
            stateChangeTracker.delete(state);
        };
    }, deps.concat(state));
}
/**
 * @deprecated
 * Please use "useStateEffect" instead
 */
export function useStateTransition() {
    const state = arguments[0];
    const transitions = arguments[1];
    const cb = arguments[2] || arguments[1];
    const deps = Array.isArray(arguments[arguments.length - 1])
        ? arguments[arguments.length - 1]
        : [];
    if (typeof transitions === "function") {
        const stateChange = stateChangeTracker.get(state);
        return React.useEffect(() => {
            const disposer = cb(state, stateChange === null || stateChange === void 0 ? void 0 : stateChange.action, stateChange === null || stateChange === void 0 ? void 0 : stateChange.prevState);
            return () => {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                stateChangeTracker.delete(state);
            };
        }, deps.concat(state));
    }
    if (typeof transitions === "string" || Array.isArray(transitions)) {
        const statesList = Array.isArray(transitions)
            ? transitions
            : [transitions];
        const currentState = state;
        const isMatch = statesList.includes(state.state);
        return React.useEffect(
        // @ts-ignore
        () => {
            let disposer = isMatch ? cb(currentState) : undefined;
            return () => {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                stateChangeTracker.delete(state);
            };
        }, deps.concat(isMatch));
    }
    return React.useEffect(() => {
        var _a;
        const stateChange = stateChangeTracker.get(state);
        const currentState = state;
        const prevState = stateChange === null || stateChange === void 0 ? void 0 : stateChange.prevState;
        const action = stateChange === null || stateChange === void 0 ? void 0 : stateChange.action;
        const stateTransition = (prevState === null || prevState === void 0 ? void 0 : prevState.state) &&
            (action === null || action === void 0 ? void 0 : action.type) &&
            ((_a = transitions[prevState === null || prevState === void 0 ? void 0 : prevState.state]) === null || _a === void 0 ? void 0 : _a[action === null || action === void 0 ? void 0 : action.type]);
        const actionTransition = prevState && typeof transitions[prevState.state] === "string"
            ? transitions[prevState.state]
            : prevState && Array.isArray(transitions[prevState.state])
                ? transitions[prevState.state]
                : undefined;
        if (stateTransition) {
            const targetStates = Array.isArray(stateTransition)
                ? stateTransition
                : [stateTransition];
            if (targetStates.includes(currentState.state)) {
                const disposer = cb(currentState, action, prevState);
                return () => {
                    disposer === null || disposer === void 0 ? void 0 : disposer();
                    stateChangeTracker.delete(state);
                };
            }
        }
        else if (actionTransition && actionTransition === (action === null || action === void 0 ? void 0 : action.type)) {
            const disposer = cb(action, prevState);
            return () => {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                stateChangeTracker.delete(state);
            };
        }
        else if (actionTransition &&
            Array.isArray(actionTransition) &&
            actionTransition.includes(action === null || action === void 0 ? void 0 : action.type)) {
            const disposer = cb(action, prevState);
            return () => {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                stateChangeTracker.delete(state);
            };
        }
        return () => {
            stateChangeTracker.delete(state);
        };
    }, deps.concat(state));
}
export const managerContext = React.createContext(null);
// We have to type as any as States<any, any> throws error not matching
// the explicit context
export const useDevtools = (id, reducer) => {
    const manager = React.useContext(managerContext);
    const [state] = reducer;
    React.useEffect(() => () => manager === null || manager === void 0 ? void 0 : manager.dispose(id), [id, manager]);
    if (manager) {
        upsertStateChange(reducer[0], {
            debugDispatch: (action, isIgnored) => {
                manager === null || manager === void 0 ? void 0 : manager.onMessage(id, {
                    type: "dispatch",
                    action,
                    ignored: isIgnored,
                });
            },
        });
    }
    React.useEffect(() => {
        manager === null || manager === void 0 ? void 0 : manager.onMessage(id, {
            type: "state",
            state,
        });
    }, [id, manager, state]);
    return reducer;
};
//# sourceMappingURL=hooks.js.map